"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/utils/cn";
import { isAdmin } from "@/utils/auth";

export interface ProfileDropdownProps {
  userName?: string;
  userEmail?: string;
  onLogoutClick: () => void;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  userName = "User",
  userEmail = "user@example.com",
  onLogoutClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  // Click-outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Keyboard navigation (Escape key)
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen]);

  // Check if user is admin
  useEffect(() => {
    setIsUserAdmin(isAdmin());
  }, []);

  const handleLogoutClick = () => {
    onLogoutClick();
    closeDropdown();
  };

  const handleUserManagementClick = () => {
    router.push("/admin/users");
    closeDropdown();
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Profile Icon Button */}
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
        aria-label="User profile menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="text-sm font-semibold">
          {userName.charAt(0).toUpperCase()}
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            "absolute right-0 mt-2 w-64 bg-white rounded-theme-md shadow-lg border border-secondary-200 z-50",
            "animate-in fade-in slide-in-from-top-2 duration-200"
          )}
          role="menu"
          aria-orientation="vertical"
        >
          {/* User Info Section */}
          <div className="px-theme-md py-theme-sm border-b border-secondary-200">
            <p className="text-sm font-semibold text-secondary-900">
              {userName}
            </p>
            <p className="text-xs text-secondary-600 truncate">{userEmail}</p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* User Management - Admin only */}
            {isUserAdmin && (
              <button
                onClick={handleUserManagementClick}
                className="w-full text-left px-theme-md py-2 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors focus:outline-none focus:bg-secondary-100 flex items-center gap-2"
                role="menuitem"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                User Management
              </button>
            )}

            <button
              onClick={handleLogoutClick}
              className="w-full text-left px-theme-md py-2 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors focus:outline-none focus:bg-secondary-100 flex items-center gap-2"
              role="menuitem"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
